import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { PosterTextData } from '@/types/canvas';
import type { PosterStyleDefinition } from '../config/posterStyles';
import { useTranslation } from 'react-i18next';

interface TextSettingsPanelProps {
  posterStyle?: PosterStyleDefinition;
  textData: PosterTextData;
  onTextChange: (data: PosterTextData) => void;
  centerLat?: number;
  centerLng?: number;
}

export function TextSettingsPanel({ posterStyle, textData, onTextChange, centerLat, centerLng }: TextSettingsPanelProps) {
  const { t } = useTranslation();
  
  const labels = [
    { key: 'title' as const, labelKey: 'settings.textFields.title' },
    { key: 'subtitle' as const, labelKey: 'settings.textFields.subtitle' },
    { key: 'detail' as const, labelKey: 'settings.textFields.detail' },
  ];

  const formatCoordinates = (lat: number, lng: number): string => {
    const latDir = lat >= 0 ? 'N' : 'S';
    const lngDir = lng >= 0 ? 'E' : 'W';
    const latAbs = Math.abs(lat);
    const lngAbs = Math.abs(lng);
    
    // Formátování na 3 desetinná místa
    const latFormatted = latAbs.toFixed(3);
    const lngFormatted = lngAbs.toFixed(3);
    
    return `${latFormatted}°${latDir} / ${lngFormatted}°${lngDir}`;
  };

  const handleInsertMapCenter = () => {
    if (centerLat !== undefined && centerLng !== undefined) {
      const coordinates = formatCoordinates(centerLat, centerLng);
      onTextChange({ ...textData, detail: coordinates });
    }
  };

  if (!posterStyle) {
    return (
      <p className="text-muted-foreground text-sm">
        {t('settings.selectPosterStyleFirst')}
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {labels.map(({ key, labelKey }) => {
        const template = posterStyle.textBlocks.find((block) => block.slot === key);
        const placeholder = template?.defaultValue ?? '';
        const isDetailField = key === 'detail';

        return (
          <div key={key} className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor={`text-input-${key}`}>{t(labelKey)}</Label>
              {isDetailField && centerLat !== undefined && centerLng !== undefined && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleInsertMapCenter}
                  className="text-xs"
                >
                  {t('settings.insertMapCenter')}
                </Button>
              )}
            </div>
            <Input
              id={`text-input-${key}`}
              value={textData[key] || ''}
              onChange={(e) => onTextChange({ ...textData, [key]: e.target.value })}
              placeholder={placeholder}
            />
          </div>
        );
      })}
    </div>
  );
}